import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import type { ChapterEnum, WorkOrderMtrInflationData } from 'gql/graphql';
import { ActKc2StatusEnum, type WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useGetActKC2MtrForInflation } from 'pages/Project/UerProject/WorkOrder/InflationDistributionModal/hooks/useGetActKC2MtrForInflation';
import { useInflationDistributionColumns } from 'pages/Project/UerProject/WorkOrder/InflationDistributionModal/hooks/useInflationDistributionColumns';
import { useUpdateInflationMtr } from 'pages/Project/UerProject/WorkOrder/InflationDistributionModal/hooks/useUpdateInflationMtr';
import { IconClose, IconQuestion, Button, HeaderSearchBar, Loader, Popover, Switch } from 'uikit';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import { agGridAdapter } from '@consta/ag-grid-adapter/agGridAdapter';
import cn from 'classnames';
import debounce from 'debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getUerRowClass } from '../../helpersUerProject';
import { useGetActKc2 } from '../hooks/useGetActKc2';
import { useSeasons } from '../hooks/useSeasons';

import './style/InflationDistributionTable.css';

import Styles from './style/InflationDistributionModal.module.css';

export interface SearchWhere {
    value: string;
}

export interface InflationDistributionModalProps {
    aktKC2Rules: IUseAccess['aktKC2'];
    statusWorkOrder?: WorkOrderStatusEnum;
    actKC2Id: number;
    onClose?: () => void;
    onRefresh?: () => void;
    isOpened: boolean;
    number?: number | string | null;
    chapter: ChapterEnum;
    workOrderSubObjectId: number;
}

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
};

export interface IRowDataChangeItems {
    inflationSeasonId: number | null;
    workOrderPriceId: number;
    isChangeRow: boolean;
}

const getRowId = ({ data }: { data: WorkOrderMtrInflationData }) => data?.id;

export const InflationDistributionModal = ({
    actKC2Id,
    chapter,
    workOrderSubObjectId,
    onClose,
    onRefresh,
    isOpened,
    number,
}: InflationDistributionModalProps) => {
    const dispatch = useDispatch();
    const { orderId } = useParams();
    const [searchString, setSearchString] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [queryWhere, setQueryWhere] = useState<SearchWhere | undefined>();
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);
    const [changedRows, setChangedRows] = useState<IRowDataChangeItems[]>([]);
    const [seasons, seasonsStatus] = useSeasons(Number(orderId), false);
    const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] = useState(false);
    const [isModalDataChange, setChangeData] = useState(false);
    const [isResetBaseInflation, setIsResetBaseInflation] = useState(false);
    const switchPopoverRef = useRef(null);

    const { getActKC2Response, getActKC2Status, getActKC2Reset } = useGetActKc2(actKC2Id);

    const addChangeRowInflation = useCallback(() => {
        setChangeData(true);
    }, []);

    const dataResponse = useMemo(() => getActKC2Response?.uerActKC2?.data, [getActKC2Response?.uerActKC2?.data]);

    const isDistributionOfMtrInflation = useMemo(
        () => Boolean(dataResponse?.isDistributionOfMtrInflation),
        [dataResponse?.isDistributionOfMtrInflation],
    );

    const [switchChecked, setSwitchChecked] = useState(isDistributionOfMtrInflation);

    useEffect(() => {
        if (getActKC2Status === RequestStatus.SUCCESS) {
            setSwitchChecked(isDistributionOfMtrInflation);
        }
    }, [isDistributionOfMtrInflation, getActKC2Status]);

    const styleOptions = agGridAdapter({
        size: 'm',
        borderBetweenColumns: false,
        borderBetweenRows: true,
        headerHeight: 40,
    });

    const handleOnEditCancel = useCallback(() => {
        setIsSubmitted(false);
        if (isModalDataChange) {
            setIsOpenConfirmationDialog(true);
        } else {
            onClose?.();
        }
    }, [isModalDataChange, onClose]);

    const modalTitle = useMemo(
        () =>
            getActKC2Status === RequestStatus.FETCHING
                ? ''
                : `Инфляция МТР в акте ${descriptionNumberAndDate(number, dataResponse?.date)}`,
        [getActKC2Status, number, dataResponse],
    );

    const onCheckedChange = useCallback(
        ({ checked }) => {
            if (isDistributionOfMtrInflation) {
                if (switchChecked) {
                    setIsPopoverVisible(true);
                } else {
                    setSwitchChecked(checked);
                }
            } else {
                setSwitchChecked(checked);
            }
            setChangeData(true);
        },
        [isDistributionOfMtrInflation, switchChecked],
    );

    const isDisabledSwitch = dataResponse?.status === ActKc2StatusEnum.Accepted;

    const handleSearch = useCallback(({ value }) => {
        setQueryWhere(() => ({
            value,
        }));
    }, []);

    const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);

    const handleChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    const [actKC2MtrForInflationData, statusInflation, , , changeFilterHandler] = useGetActKC2MtrForInflation(
        actKC2Id,
        workOrderSubObjectId,
        chapter,
        queryWhere,
    );

    const yearInflationActKC2 = dataResponse?.inflationSeason.year;

    const { columns } = useInflationDistributionColumns({
        switchChecked,
        seasons,
        addChangeRowInflation,
        setChangedRows,
        actKC2MtrForInflationData,
        changeFilterHandler,
        yearInflationActKC2,
    });

    const updateChangedRows = useMemo(
        () =>
            Object.values(changedRows)
                .filter((item) => item.isChangeRow)
                .map(({ isChangeRow, ...rest }) => rest),
        [changedRows],
    );

    const actKC2MtrItems = switchChecked ? updateChangedRows : [];

    const { dataUpdateInflation, status, reset, error, makeRequest, isSubmittedData, handleCreateReport } =
        useUpdateInflationMtr({
            actKC2Id,
            useInflationMTR: switchChecked,
            actKC2MtrItems,
        });

    useEffect(() => {
        if (isSubmitted && status === RequestStatus.SUCCESS && dataUpdateInflation?.result === true) {
            onRefresh?.();
            getActKC2Reset(undefined);
            onClose?.();
        }
    }, [dataUpdateInflation?.result, dispatch, getActKC2Reset, isSubmitted, onClose, onRefresh, status]);

    const isLoading = statusInflation === RequestStatus.FETCHING && seasonsStatus === RequestStatus.FETCHING;

    const saveChangesHandler = useCallback(() => {
        setIsSubmitted(true);
        makeRequest();
    }, [makeRequest]);

    const handleClosePopover = useCallback(() => {
        if (!isOpenConfirmationDialog) {
            setIsPopoverVisible(false);
        }
    }, [isOpenConfirmationDialog]);

    const handleSwitchChecked = useCallback(() => {
        setIsResetBaseInflation(true);
        handleClosePopover();
        setSwitchChecked(false);
    }, [handleClosePopover]);

    const handleClearSearch = useCallback(() => {
        if (searchString) {
            setSearchString('');
            handleSearch('');
        }
    }, [handleSearch, searchString]);

    const handleOnSubmitConfirmationDialog = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleCancelConfirmationDialog = useCallback(() => {
        setIsOpenConfirmationDialog(false);
    }, []);

    const btnConfirmDisabled = useMemo(() => {
        if (!switchChecked && isResetBaseInflation) {
            return false;
        }

        return !updateChangedRows?.length;
    }, [isResetBaseInflation, switchChecked, updateChangedRows?.length]);

    return (
        <>
            <ModalDialog
                title={modalTitle}
                onConfirm={saveChangesHandler}
                onClose={handleOnEditCancel}
                btnConfirmLabel="Применить"
                withTitleCloseButton
                hasOverlay
                isCommit={status === RequestStatus.FETCHING}
                isOpen={isOpened}
                isOnClickOutsideDisable
                className={Styles.modal}
                btnConfirmDisabled={btnConfirmDisabled}
            >
                <div className={Styles.modalContent}>
                    <div className={Styles.wrapperSwitch}>
                        <div ref={switchPopoverRef}>
                            <Switch
                                label="Индивидуальное начисление инфляции для позиций МТР"
                                checked={switchChecked}
                                size="m"
                                onChange={onCheckedChange}
                                style={{ marginRight: 8 }}
                                disabled={isDisabledSwitch}
                            />
                        </div>
                        {isDistributionOfMtrInflation && isPopoverVisible && (
                            <Popover
                                direction="downCenter"
                                spareDirection="downCenter"
                                offset="2xs"
                                arrowOffset={0}
                                onClickOutside={handleClosePopover}
                                isInteractive
                                equalAnchorWidth
                                anchorRef={switchPopoverRef}
                            >
                                <div className="popoverWrapContent">
                                    <p>
                                        Для позиций МТР была индивидуально начислена инфляция. Вы уверены, что хотите
                                        сбросить выбранные значения для всех позиций?
                                    </p>
                                    <div className="popoverWrapButton">
                                        <Button onClick={handleSwitchChecked} className="dark" label="Сбросить" />
                                        <Button
                                            onClick={() => setIsPopoverVisible(false)}
                                            className="dark"
                                            label="Отменить"
                                        />
                                    </div>
                                </div>
                            </Popover>
                        )}
                        <ComponentWithTooltip tooltipText="При отключении индивидуального начисления, все ранее введенные значения будут сброшены">
                            <IconQuestion view="secondary" />
                        </ComponentWithTooltip>
                    </div>
                    <div className={Styles.wrapperSearch}>
                        <HeaderSearchBar
                            className={Styles.search}
                            placeholder="Поиск МТР"
                            label="поиск"
                            value={searchString}
                            onChange={handleChange}
                            onSearch={handleSearch}
                        />
                        {searchString && (
                            <Button
                                label="Очистить"
                                view="ghost"
                                iconLeft={IconClose}
                                form="round"
                                size="xs"
                                onlyIcon
                                onClick={handleClearSearch}
                                className={Styles.clearSearch}
                            />
                        )}
                    </div>

                    <div style={{ height: '340px' }} className={cn('ag-theme-alpine', Styles.inflationGridWrapper)}>
                        {isLoading ? (
                            <Loader />
                        ) : (
                            <AgGridTable
                                {...styleOptions}
                                getRowId={getRowId}
                                rowData={actKC2MtrForInflationData}
                                columnDefs={columns}
                                defaultColDef={defaultColDef}
                                rowClassRules={getUerRowClass()}
                                overlayNoRowsTemplate="Нет данных"
                                suppressCellFocus
                                icons={AG_GRID_ICON_SETUP}
                            />
                        )}
                    </div>
                </div>
            </ModalDialog>
            <ErrorHandlingModal
                titleText={modalTitle}
                isSubmitted={isSubmittedData}
                statusRequest={status}
                dataRequest={dataUpdateInflation}
                errorsRequest={error}
                handleCloseModalError={reset}
                handleCreateReport={handleCreateReport}
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleOnSubmitConfirmationDialog}
                onClose={handleCancelConfirmationDialog}
            />
        </>
    );
};

InflationDistributionModal.displayName = 'InflationDistributionModal';
